import { cn } from '@utima/ui';
import { memo } from 'react';

import { usePriorityLabels } from '../hooks/usePriorityLabels';
import { weightToPriorityScale, type PriorityScale } from '../lib/priority';

const BAR_HEIGHTS = [4, 6, 8, 10, 12];

type PriorityBarsProps = {
  scale: PriorityScale;
  variant?: 'default' | 'muted';
  className?: string;
};

export const PriorityBars = memo(function PriorityBars({
  scale,
  variant = 'default',
  className,
}: PriorityBarsProps) {
  const isMuted = variant === 'muted';

  return (
    <div className={cn('flex items-end gap-0.5', className)}>
      {BAR_HEIGHTS.map((height, index) => {
        const barScale = (index + 1) as PriorityScale;
        const isActive = scale >= barScale;

        return (
          <span
            key={barScale}
            className={cn(
              'w-1 rounded-sm',
              isActive
                ? isMuted
                  ? 'bg-muted-fg/50'
                  : 'bg-primary'
                : 'bg-muted-fg/20',
            )}
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
});

type PriorityIndicatorProps = {
  weight?: number;
  scale?: PriorityScale;
  variant?: 'default' | 'muted';
  showLabel?: boolean;
};

export const PriorityIndicator = memo(function PriorityIndicator({
  weight = 0.5,
  scale,
  variant = 'default',
  showLabel = true,
}: PriorityIndicatorProps) {
  const priorityLabels = usePriorityLabels();
  const resolvedScale = scale ?? weightToPriorityScale(weight);
  const label = priorityLabels[resolvedScale];
  const isMuted = variant === 'muted';

  return (
    <div className='flex items-center gap-2'>
      <div aria-label={label} title={label}>
        <PriorityBars scale={resolvedScale} variant={variant} />
      </div>
      {showLabel && (
        <span
          className={cn(
            'text-xs font-medium',
            isMuted ? 'text-muted-fg' : 'text-foreground',
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
});
